import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { groupWaitingCards, type WaitingCardTask } from './waiting-cards';
import { asksNotOnTasks, type OpenAsksRead, type WebOpenAsk } from './open-asks';

// THE "Action needed" numbers on web — the ONE place behind the island pill (and its card's header) and the
// Tasks screen's Action needed tab badge, so the two can never state different numbers for the same input. The
// phones have the same functions (iOS `ActionNeededCount.split`, Android `actionNeededSplit`), feeding their pill,
// tab, home widgets and Live Activity; the Watch snapshot and the native-data aggregate use common-lib
// `actionNeededCount`. Pure, no runtime imports.
//
// ⚠️ The spec is `native-apps/shared/outreach-needs-you/contract.json` (rule 1), and `test/outreach-needs-you.test.ts`
// runs its `splitVectors` in place. A conversation that waits on the owner is a THREAD in stage NEEDS_YOU on the
// Outreach screen, never a task-style card — so the outreach-only questions (`/api/tasks` `outreachQuestions`) are
// no longer counted here, and no longer drawn by the island or the Action needed tab. They are PEOPLE now: the
// thread their Decision names is NEEDS_YOU, and `peopleWaitingCount` (lib/outreach-stage.ts) counts it.
//
// The TASKS half, counted in the things the reader has to deal with:
//  - question CARDS over the waiting tasks — one per open question (one question blocking three tasks is 1), plus one
//    per waiting task that holds no question (`groupWaitingCards`);
//  - the open asks (`lib/open-asks.ts`), minus any a waiting task already asks.
// A capped list still counts everything: `max(kept, (total ?? list) − dropped)`. An absent read adds nothing.

/** An outreach-only question as the wire carries it (`/api/tasks` `outreachQuestions`). Still parsed by
 *  `lib/tasks.ts`, though nothing on web draws or counts it any more (see above). */
export type IslandOutreachQuestion = TaskDecisionPrompt & { outreachId: string; createdDate?: string };

export interface ActionNeededCount {
  questionCards: number;
  openAsks: number;
  /** The TASKS half: `questionCards + openAsks`. */
  tasks: number;
  /** The asks that survive the dedupe — what a list draws as cards. */
  asksKept: WebOpenAsk[];
}

export function actionNeededCount(waiting: readonly WaitingCardTask[], asks?: OpenAsksRead | null): ActionNeededCount {
  const questionCards = groupWaitingCards(waiting).length;
  const askPart = asksNotOnTasks(waiting as { decisionPrompts?: { decisionId: string }[] }[], asks?.asks, asks?.total);
  return {
    questionCards,
    openAsks: askPart.count,
    tasks: actionNeededSplit(questionCards, askPart.count, 0).tasks,
    asksKept: askPart.asks,
  };
}

/** The two halves the pill shows, and the one number everything else shows. */
export interface ActionNeededSplit {
  tasks: number;
  people: number;
  /** What a single-number surface shows (the app-icon badge, Live Activity, home widgets on the phones). */
  total: number;
}

/**
 * Contract rule 1, verbatim: `tasks = questionCards + openAsks`, `people` = the people count of rule 2
 * (`peopleWaitingCount`), `total = tasks + people`. The outreach-only questions are NOT an input: they are people.
 */
export function actionNeededSplit(questionCards: number, openAsks: number, people: number): ActionNeededSplit {
  const tasks = Math.max(0, questionCards) + Math.max(0, openAsks);
  const peopleCount = Math.max(0, people);
  return { tasks, people: peopleCount, total: tasks + peopleCount };
}

/** One drawn segment of the orange pill (contract rule 3). */
export interface WaitingPillSegment {
  kind: 'tasks' | 'people';
  count: number;
  /** The segment's accessibility string key, `{count}`-interpolated (the singular keys carry no param). */
  a11yKey: 'agentsIsland.taskWaiting' | 'agentsIsland.tasksWaiting' | 'agentsIsland.personWaiting' | 'agentsIsland.peopleWaiting';
  /** The glyph leading the number, if any. Only the PEOPLE segment, and only beside a tasks segment, wears one (the
   *  lucide `user`): it is there to tell two numbers apart, so a lone number carries none. */
  icon: 'user' | null;
}

/**
 * The orange pill's segments, leading to trailing: TASKS, then PEOPLE. A segment whose number is 0 is not drawn —
 * and the ` · ` separator is drawn only BETWEEN two drawn segments, so a zero half takes its separator with it.
 * The pill's accessibility label is these segments' strings joined with ', ', then `agentsIsland.waiting`.
 * At most one icon (`pillSegmentVectors`): the people segment's `user`, when both segments are drawn.
 */
export function waitingPillSegments(split: Pick<ActionNeededSplit, 'tasks' | 'people'>): WaitingPillSegment[] {
  const segments: WaitingPillSegment[] = [];
  if (split.tasks > 0) {
    segments.push({
      kind: 'tasks',
      count: split.tasks,
      a11yKey: split.tasks === 1 ? 'agentsIsland.taskWaiting' : 'agentsIsland.tasksWaiting',
      icon: null,
    });
  }
  if (split.people > 0) {
    segments.push({
      kind: 'people',
      count: split.people,
      a11yKey: split.people === 1 ? 'agentsIsland.personWaiting' : 'agentsIsland.peopleWaiting',
      icon: split.tasks > 0 ? 'user' : null,
    });
  }
  return segments;
}
